import { Box, Button, CircularProgress } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import _ from 'underscore';

import Collapsible from 'components/collapsible';
import LoadingSpinner from 'components/loading-spinner';
import { getAeratorRegistrationLog, putRegistrationLogAerator } from 'views/production-systems/api/productionSystems';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { AeratorsProvider } from 'views/self-service/features/aerators/context/aerators-context';

import RegistrationHeader from 'views/production-systems/components/registration-header';
import ListAerators from 'views/production-systems/features/farm-setup/components/list-aerators';

import { Aerator } from 'views/production-systems/features/farm-setup/types/aerators.type';
import {
  PondAeratorAssigments,
  UpdateAeratorInputForm,
  UpdateAeratorsInput,
  UpdateAssignedAerators,
  UpdateAssignedAeratorsInput,
} from 'views/production-systems/features/farm-setup/types/update-aerators-input.type';

type Props = {
  logId: number;
  eventNr: number;
  handleClose?: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
};

type ParamTypes = {
  globalSiteId: string;
};

const EditAerators: React.FC<Props> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const intl = useIntl();
  const { lockedPonds, registrationDate, lockPonds, releasePonds } = usePondsState();
  const [confirm, setConfirm] = useState<boolean>(false);

  // #region FETCH

  const { data: aeratorAssignment, isLoading } = useGet(
    ['getAeratorRegistration', params.globalSiteId, logId],
    ({ queryKey }) => getAeratorRegistrationLog(String(queryKey[1]), Number(queryKey[2]))
  );

  useEffect(() => {
    if (aeratorAssignment && canEdit) lockPonds(aeratorAssignment.pondAeratorAssignments.map((pond) => pond.pondId));
  }, [aeratorAssignment, canEdit, lockPonds]);

  // #endregion

  // #region HOOKS
  const pondsWithAerators = useMemo(() => {
    return _.chain(aeratorAssignment?.pondAeratorAssignments)
      .sortBy('pondName')
      .value()
      .map((pond) => {
        return {
          pondId: pond.pondId,
          pondName: pond.pondName,
          pondCurrentBiomass: pond.pondCurrentBiomass,
          assignedAerators: pond.assignedAerators.map((aerator) => {
            return {
              numberOfAerators: aerator.quantity,
              aeratorId: aerator.aeratorId,
              aeratorName: aerator.aeratorName,
              aeratorBrand: aerator.aeratorBrand,
              horsepower: aerator.horsepower,
              supportedBiomass: aerator.supportedBiomass,
              aeratorTypeId: aerator.aeratorTypeId,
              edit: false,
              isDeleted: aerator.quantity === 0,
            };
          }),
        };
      }) as UpdateAeratorsInput[];
  }, [aeratorAssignment]);
  // #endregion

  // #region FORMS
  const defaultValues = {
    ponds: pondsWithAerators,
  };

  const form = useForm<UpdateAeratorInputForm>({
    defaultValues: defaultValues,
    shouldUnregister: false,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const { fields: rows } = useFieldArray({
    control: control,
    name: `ponds`,
  });

  const watchPonds = useWatch({ control, name: 'ponds' }) || [];
  // #endregion

  // #region POST DATA

  const { isPending: isAssignLoading, mutate: assignAeratorsToPonds } = usePost<
    Aerator,
    { globalSiteId: string; logId: number; request: UpdateAssignedAeratorsInput }
  >(
    (request) => putRegistrationLogAerator(request.globalSiteId, request.logId, request.request),
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      aeratorAssignment && releasePonds(aeratorAssignment.pondAeratorAssignments.map((pond) => pond.pondId));
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  // #region HANDLERS
  const handleSubmitAeratorsData = (request: { ponds: UpdateAeratorsInput[] }) => {
    const assignments: PondAeratorAssigments[] = request.ponds
      .map((pond) => {
        const aeratorsForPond = pond.assignedAerators.reduce((acc: UpdateAssignedAerators[], aerator) => {
          if (!aerator.isDeleted) {
            acc.push({
              aeratorId: aerator.aeratorId,
              quantity: Number(aerator.numberOfAerators),
            });
          } else if (aerator.isDeleted && !aerator.isNew) {
            acc.push({
              aeratorId: aerator.aeratorId,
              quantity: 0,
            });
          }
          return acc;
        }, []);

        return {
          pondId: pond.pondId,
          assignedAerators: aeratorsForPond,
        };
      })
      .filter((pond) => pond.assignedAerators.length > 0)
      .map((pond) => {
        return {
          pondId: pond.pondId,
          assignedAerators: pond.assignedAerators.map((aerator) => ({
            aeratorId: aerator.aeratorId,
            quantity: Number(aerator.quantity),
          })),
        } as PondAeratorAssigments;
      });

    const pondsWithAssignments: UpdateAssignedAeratorsInput = {
      date: registrationDate.format('YYYY-MM-DD'),
      pondAeratorAssignments: assignments,
    };

    if (pondsWithAssignments.pondAeratorAssignments.length === 0) return;

    assignAeratorsToPonds({ globalSiteId: params.globalSiteId, logId: logId, request: pondsWithAssignments });
  };

  const isDisableSubmit = watchPonds
    .flatMap((pond) => pond.assignedAerators || [])
    .every((aerator) => aerator.isNew && aerator.isDeleted);
  // #endregion

  useEffect(() => {
    reset({ ponds: pondsWithAerators });
  }, [pondsWithAerators]);

  return isLoading ? (
    <LoadingSpinner />
  ) : lockedPonds.notGranted.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <>
        <PondLockTimer handleDialogClose={handleClose} />
        <RegistrationHeader dateCreated={registrationDate} />
        <FormProvider {...form}>
          <form onSubmit={handleSubmit((data) => handleSubmitAeratorsData(data))}>
            <Box display={'flex'} flexDirection={'column'} gap={4}>
              {rows.map((pond, idx) => {
                const { pondCurrentBiomass, pondName } = pond;
                return (
                  <Collapsible hideHeaderBorder label={`${pondName}`} key={idx} open={idx === 0}>
                    <ListAerators fieldIndex={idx} pondCurrentBiomass={pondCurrentBiomass} />
                  </Collapsible>
                );
              })}
              <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
                <Button
                  type="submit"
                  disabled={isAssignLoading || !isValid || !isDirty || isDisableSubmit}
                  startIcon={isAssignLoading ? <CircularProgress color="inherit" size={18} /> : null}>
                  <FormattedMessage id="general.submit" values={{ type: '' }} />
                </Button>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </>
    </Box>
  );
};

const ManageEditAeratorsContainer: React.FC<Props> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  return (
    <AeratorsProvider>
      <EditAerators handleClose={handleClose} logId={logId} eventNr={eventNr} canEdit={canEdit} dateCreated={dateCreated} />
    </AeratorsProvider>
  );
};

export default React.memo(ManageEditAeratorsContainer);
